import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import TripAiMessagesContainer from '../trip_ai_messages/components/TripAiMessagesContainer';
import { createDraftTripWithMessage, type RecentMessagePayload } from '../trip_ai_messages/api/messages';

const TripAiMessagesNewPage: React.FC = () => {
  const { theme } = useAITTheme();
  const history = useHistory();
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentMessages] = useState<RecentMessagePayload[]>([]);
  const styles = useMemo(() => getStyles(theme), [theme]);
  const canSend = inputValue.trim().length > 0 && !sending;

  const handleSend = async () => {
    const text = inputValue.trim();
    if (sending || text === '') return;

    setSending(true);
    setError(null);
    try {
      const result = await createDraftTripWithMessage(text, buildRecentMessagesPayload(recentMessages));
      history.replace({
        pathname: `/trips/${result.tripId}/ai_messages`,
        state: { initialMessage: text }
      });
    } catch (e) {
      const message =
        e instanceof Error && e.message ? e.message : '送信に失敗しました。時間をおいて再度お試しください。';
      setError(message);
    } finally {
      setSending(false);
    }
  };

  if (!theme) {
    return <Loading />;
  }

  return (
    <TripAiMessagesContainer
      tripId="new"
      trip={null}
      pusherKey=""
      pusherChannelPrefix=""
      headerTitle="新規チャット"
      subtitle="最初のメッセージを入力してください。送信後にチャット画面へ遷移します。"
      enableMessaging={false}
      isNew
    >
      <div style={styles.body}>
        <textarea
          value={inputValue}
          onChange={event => setInputValue(event.target.value)}
          placeholder="例）来週水曜に東京出張があります。名古屋から朝移動で、1泊のホテルを探してほしい。"
          rows={4}
          style={styles.textarea}
          onKeyDown={event => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.actions}>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              opacity: canSend ? 1 : 0.5,
              cursor: canSend ? 'pointer' : 'not-allowed'
            }}
            onClick={handleSend}
            disabled={!canSend}
          >
            {sending ? '送信中...' : '送信してチャットを開始'}
          </button>
        </div>
      </div>
    </TripAiMessagesContainer>
  );
};

const getStyles = (theme: AITTheme) => ({
  body: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${theme.grayBorderColor}`,
    resize: 'none' as const,
    fontFamily: 'inherit'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  primaryButton: {
    backgroundColor: theme.linkColor,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontWeight: 600 as const,
    cursor: 'pointer'
  },
  error: {
    color: theme.dangerColor,
    fontSize: '13px'
  }
});

export default TripAiMessagesNewPage;

const buildRecentMessagesPayload = (history: RecentMessagePayload[]): RecentMessagePayload[] => {
  if (!history || history.length === 0) return [];
  return history.slice(-20);
};
